import { useDateQuery } from "@ls/date-module";
import { Provider as GridStateLegacyProvider } from "@ls/grid-state-module";
import { Provider as GridStateAzureProvider } from "@ls/grid-state-module-azure";
import LoadingMask from "@ls/react-loading-mask";
import Dialog from "@ls/disclaimer-service";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import Home from "scenes/Home";
import {
  requestAnalyticValues,
  setDefinitions as setAnalyticDefinitions,
} from "state/analytics/actions";
import {
  requestBookBasisValues,
  setDefinitions as setBookAnalyticDefinitions,
} from "state/bookAnalytics/actions";
import AnalyticDefinition from "state/analytics/AnalyticDefinition";
import BookAnalyticDefinition from "state/bookAnalytics/BookAnalyticDefinition";
import LastBuySellDefinition from "state/lastBuySell/LastBuySellDefinition";
import RealizedGlDefinitions from "state/realizedGL/RealizedGlDefinitions";
import { selectDefinitions as selectBookAnalyticDefinitions } from "state/bookAnalytics/selectors";
import { selectDefinitions as selectAnalyticsDefinitions } from "state/analytics/selectors";
import {
  setAppDecimal,
  setAppTitle,
  setBandedRows,
  setDisableCustomGroupSort,
  setGridFontSize,
  setGridHeaderHeight,
  setGridRowHeight,
  setGridVerticalLines,
  setHideGridSideBar,
  setListenScenarioAppTalk,
  setListenEntityGroupAppTalk,
  setLoadDataOnLaunch,
  setMinInceptionDate,
  startRequests,
  toggleSelectionDrawer,
} from "state/app/actions";
import {
  selectAppDecimal,
  selectAppTitle,
  selectListenScenarioAppTalk,
  selectListenEntityGroupAppTalk,
} from "state/app/selector";
import {
  requestAttributeValues,
  requestEntityAttributes,
  setDefinitions as setAttributesDefinitions,
} from "state/attributes/actions";
import AttributeDefinition from "state/attributes/AttributeDefinition";
import { selectDefinitions as selectAttributesDefinitions } from "state/attributes/selectors";
import {
  requestBetaDecodes,
  setDefinitions as setBetasDefinitions,
} from "state/betas/actions";
import { setDefinitions as setScenarioAnalysisDefinitions } from "state/scenarioAnalysis/actions";
import { setDefinitions as setScan2Definitions } from "state/scan2/actions";
import BetaDefinition from "state/betas/BetaDefinition";
import Scan2Definition from "state/scan2/Scan2Definition";
import ScenarioAnalysisDefinition from "state/scenarioAnalysis/ScenarioAnalysisDefinition";
import { selectDefinitions as selectBetasDefinitions } from "state/betas/selectors";
import { selectDefinitions as selectScan2Definitions } from "state/scan2/selectors";
import { selectDefinitions as selectScenarioAnalysisDefinitions } from "state/scenarioAnalysis/selectors";
import { setDefinitions as setCfDefinitions } from "state/cf/actions";
import CashFlowDefinition from "state/cf/CashFlowDefinition";
import { selectDefinitions as selectCfDefinitions } from "state/cf/selectors";
import { setDefinitions as setDurationToTargetDefinitions } from "state/durationToTarget/actions";
import DurationToTargetDefinition from "state/durationToTarget/DurationToTargetDefinition";
import { selectDefinitions as selectDurationToTargetDefinitions } from "state/durationToTarget/selectors";
import {
  requestFactribPeriods,
  requestFactribTypes,
  setDefinitions as setFactribDefinitions,
} from "state/factrib/actions";
import FactorAttributionDefinition from "state/factrib/FactorAttributionDefinition";
import IssueCountDefinition from "state/issueCount/IssueCountDefinition";
import IssuerCountDefinition from "state/issuerCount/IssuerCountDefinition";
import ClientLossRstrDefinition from "state/clientLossRstr/ClientLossRstrDefinition";
import { setEnableAsOfDate } from "state/misc/actions";
import { selectEnableAsOfDate } from "state/misc/selectors";
import PerfReturnDefinition from "state/perfReturns/PerfReturnDefinition";
import PivotDefinition from "state/pivots/PivotDefinition";
import { selectDefinitions as selectFactribDefinitions } from "state/factrib/selectors";
import { selectDefinitions as selectIssueCountDefinitions } from "state/issueCount/selectors";
import { selectDefinitions as selectIssuerCountDefinitions } from "state/issuerCount/selectors";
import { selectDefinitions as selectPerfReturnsDefinitions } from "state/perfReturns/selectors";
import { selectDefinitions as selectPivotsDefinitions } from "state/pivots/selectors";
import RatingDefinition from "state/ratings/RatingDefinition";
import { selectDefinitions as selectRatingDefinitions } from "state/ratings/selectors";
import { setDefinitions as setIssueCountDefinitions } from "state/issueCount/actions";
import { setDefinitions as setIssuerCountDefinitions } from "state/issuerCount/actions";
import { setDefinitions as setClientLossRstrDefinitions } from "state/clientLossRstr/actions";
import { setDefinitions as setPerfReturnsDefinitions } from "state/perfReturns/actions";
import { setDefinitions as setPivotsDefinitions } from "state/pivots/actions";
import { setDefinitions as setRatingDefinitions } from "state/ratings/actions";
import { selectGridStateId } from "state/grid/selector";
import { setGridStateId } from "state/grid/actions";
import { useProcessColumnStateCallback } from "state/grid";
import {
  useUserPlatformEffect,
  useUserScenarioOptionsEffect,
} from "state/scenario";
import { selectScenarioOptions } from "state/scenario/selector";
import {
  selectFetchingDeleteScenarioEntities,
  selectFetchingScenarioEntityIds,
  selectFetchingScenarioState,
  selectFetchingSubmitScenarioEntities,
} from "state/scenario/selector";
import { useRequestGroupsEffect } from "state/selections";
import {
  commitSelections,
  requestEntities,
  requestGroupingLevels,
  setAsOfDate,
  setBenchmarkEntityId,
  setDisplayBenchmarks,
  setEntityIds,
  setGroup,
  setGroupType,
  setLookthru,
  setMajorMinor,
  setMode,
  setScenariosValue,
  setSideBySide,
  setSymbolicDate,
  setGlPam,
  setBenchmarkScenariosValue,
  setIsDisableBenchmarkWorkspace,
} from "state/selections/actions";
import Mode from "state/selections/Mode";
import SelectionState from "state/selections/SelectionState";
import {
  selectAsOfDateValue,
  selectUserInvestPlatformIds,
} from "state/selections/selector";
import { setDefinitions as setTeDefinitions } from "state/te/actions";
import { selectDefinitions as selectTeDefinitions } from "state/te/selectors";
import { selectDefinitions as selectLastBuySellDefinitions } from "state/lastBuySell/selectors";
import { setDefinitions as setLastBuySellDefinitions } from "state/lastBuySell/actions";
import { selectDefinitions as selectRealizedGlDefinitions } from "state/realizedGL/selectors";
import { selectDefinitions as selectClientLossRstrDefinitions } from "state/clientLossRstr/selectors";
import { setDefinitions as setRealizedGlDefinitions } from "state/realizedGL/actions";
import TrackingErrorDefinition from "state/te/TrackingErrorDefinition";
import {
  requestTradeStrategies,
  requestTradeStrategyGroups,
  setDefinitions as setTteDefinitions,
} from "state/tte/actions";
import { selectDefinitions as selectTteDefinitions } from "state/tte/selectors";
import TradeTrackingErrorDefinition from "state/tte/TradeTrackingErrorDefinition";
import { getPublicContext } from "tools";
import { useQueryClient } from "react-query";
import { requestScenarioOptions } from "state/scenario/actions";
import { useDataQueries as usePivotDataQueries } from "state/pivots/hooks";
import {
  selectUncommittedAllGroupEntities,
  selectUncommittedGroupTypeEntity,
  selectDisplayBenchmarkValue,
} from "state/selections/selector";
import appTalkSentMessage from "appTalkSentMessage";

const getUrlStateId = () => {
  const publicContext = getPublicContext();
  const pathNames = window.location.pathname.split("/").filter((s) => s.length);

  if (pathNames?.length > 0) {
    const potentialStateId = decodeURIComponent(
      pathNames[pathNames.length - 1],
    );
    if (potentialStateId !== publicContext) {
      return potentialStateId;
    }
  }
};

const urlParams = new URLSearchParams(window.location.search);
const hideGridBarOption = urlParams.get("hideGridBarOption") === "true";

const App = () => {
  const dispatch = useDispatch();
  const [auxStateRequest, setAuxStateRequest] = useState(null);
  const [disclaimer, setDisclaimer] = useState("false");
  const appTitle = useSelector(selectAppTitle);
  const decimalValue = useSelector(selectAppDecimal);
  const gridStateId = useSelector(selectGridStateId);
  const committedAsOfDate = useSelector(
    selectAsOfDateValue(SelectionState.COMMITTED),
  );
  const options = useSelector(selectScenarioOptions);
  const GridStateProvider =
    process.env.REACT_APP_HOST_ENV === "AZURE"
      ? GridStateAzureProvider
      : GridStateLegacyProvider;

  const uncommittedAsOfDate = useSelector(
    selectAsOfDateValue(SelectionState.UNCOMMITTED),
  );
  const fetchingScenarioEntityIds = useSelector(
    selectFetchingScenarioEntityIds,
  );

  const fetchingScenarioState = useSelector(selectFetchingScenarioState);
  const fetchingSubmitScenarioEntities = useSelector(
    selectFetchingSubmitScenarioEntities,
  );
  const fetchingDeleteScenarioEntities = useSelector(
    selectFetchingDeleteScenarioEntities,
  );

  const groupTypeGroups = useSelector(selectUncommittedAllGroupEntities);
  const groupTypeEntity = useSelector(selectUncommittedGroupTypeEntity);

  const displayBenchmarksValue = useSelector(
    selectDisplayBenchmarkValue(SelectionState.UNCOMMITTED),
  );
  const entityGroupOptions = useMemo(
    () => [...groupTypeGroups].sort(groupTypeEntity?.groupTypeGroupSorter),
    [groupTypeGroups, groupTypeEntity],
  );
  // Check if pivot columns are fetching for loading screen
  const [dataQueries, sbsQueries] = usePivotDataQueries();
  const isPivotFetching =
    dataQueries.some((query) => query.isFetching) ||
    sbsQueries.some((query) => query.isFetching);

  const auxStateReqSymbolicDate = auxStateRequest?.selectionState?.symbolicDate;
  const auxStateSymbolicDateQuery = useDateQuery(auxStateReqSymbolicDate);

  useRequestGroupsEffect();
  useUserPlatformEffect();
  useUserScenarioOptionsEffect();

  useEffect(() => {
    dispatch(requestAnalyticValues());
    dispatch(requestBookBasisValues());
    dispatch(requestAttributeValues());
    dispatch(requestBetaDecodes());
    dispatch(requestFactribPeriods());
    dispatch(requestFactribTypes());
    dispatch(requestGroupingLevels());
    if (process.env.REACT_APP_HOST_ENV !== "AZURE") {
      dispatch(requestTradeStrategies());
      dispatch(requestTradeStrategyGroups());
    }
  }, [dispatch]);

  useEffect(() => {
    window.document.title = appTitle;
  }, [appTitle]);

  const onChangeStateId = useCallback(
    (gridStateId) => {
      dispatch(setGridStateId(gridStateId));
      /**
       * Update URL based on gridStateId.
       * 1) Reset URL to its base states
       * 2) Append new gridStateId to URL path, if it exists
       * 3) Don't forget any query params (location.search)
       */
      const pathArr = window.location.pathname
        .split("/")
        .filter((pathSegment) => pathSegment.length);
      const baseIdx = pathArr.findIndex(
        (pathSegment) => pathSegment === getPublicContext(),
      );
      pathArr.splice(baseIdx + 1, pathArr.length); //path is now reset
      if (gridStateId) {
        pathArr.push(encodeURIComponent(gridStateId));
      }

      if (process.env.REACT_APP_IN2_ENV === "DEV" && false) {
        window.history.pushState(
          null,
          "",
          `/${encodeURIComponent(gridStateId ?? "")}`,
        ); //must have leading slash
      } else {
        window.history.pushState(
          null,
          "",
          `/${pathArr.join("/")}${window.location.search}`,
        ); //must have leading slash
      }
    },
    [dispatch],
  );

  /**
   * For getting share url from the list of available grid states.
   */
  const getShareUrl = useCallback((gridStateId) => {
    const pathArr = window.location.pathname
      .split("/")
      .filter((pathSegment) => pathSegment.length);
    const baseIdx = pathArr.findIndex(
      (pathSegment) => pathSegment === getPublicContext(),
    );
    pathArr.splice(baseIdx + 1, pathArr.length); //path is now reset to x/y/z/accountsummaryui
    if (gridStateId) {
      pathArr.push(encodeURIComponent(gridStateId));
    }
    return `${window.location.origin}/${pathArr.join("/")}${
      window.location.search
    }`;
  }, []);

  /**
   * ensures URL state ID is applied,
   * if no url state ID is present, date is set to default PBD
   */
  useEffect(() => {
    const urlStateId = getUrlStateId();
    const disclosure = new URLSearchParams(window.location.search).get(
      "disclosure",
    );
    setDisclaimer(disclosure);
    if (urlStateId) {
      dispatch(setGridStateId(urlStateId));
    } else {
      dispatch(setSymbolicDate("PBD"));
    }
  }, [dispatch]);

  const selectionState = useSelector(
    (state) => state.Selections[SelectionState.COMMITTED],
  );
  const appState = useSelector((state) => state.App);
  const analyticsDefinitions = useSelector(
    selectAnalyticsDefinitions(SelectionState.COMMITTED),
  );

  const bookAnalyticDefinitions = useSelector(
    selectBookAnalyticDefinitions(SelectionState.COMMITTED),
  );
  const attributesDefinitions = useSelector(
    selectAttributesDefinitions(SelectionState.COMMITTED),
  );
  const betasDefinitions = useSelector(
    selectBetasDefinitions(SelectionState.COMMITTED),
  );
  const scenarioAnalysisDefinitions = useSelector(
    selectScenarioAnalysisDefinitions(SelectionState.COMMITTED),
  );
  const cfDefinitions = useSelector(
    selectCfDefinitions(SelectionState.COMMITTED),
  );
  const durationToTargetDefinitions = useSelector(
    selectDurationToTargetDefinitions(SelectionState.COMMITTED),
  );
  const factribDefinitions = useSelector(
    selectFactribDefinitions(SelectionState.COMMITTED),
  );
  const issueCountDefinitions = useSelector(
    selectIssueCountDefinitions(SelectionState.COMMITTED),
  );
  const issuerCountDefinitions = useSelector(
    selectIssuerCountDefinitions(SelectionState.COMMITTED),
  );
  const perfReturnsDefinitions = useSelector(
    selectPerfReturnsDefinitions(SelectionState.COMMITTED),
  );
  const pivotsDefinitions = useSelector(
    selectPivotsDefinitions(SelectionState.COMMITTED),
  );

  const ratingDefinitions = useSelector(
    selectRatingDefinitions(SelectionState.COMMITTED),
  );
  const teDefinitions = useSelector(
    selectTeDefinitions(SelectionState.COMMITTED),
  );
  const tteDefinitions = useSelector(
    selectTteDefinitions(SelectionState.COMMITTED),
  );
  const enableAsOfDateMisc = useSelector(
    selectEnableAsOfDate(SelectionState.COMMITTED),
  );
  const lastBuySellDefinitions = useSelector(
    selectLastBuySellDefinitions(SelectionState.COMMITTED),
  );

  const realizedGlDefinitions = useSelector(
    selectRealizedGlDefinitions(SelectionState.COMMITTED),
  );

  const clientLossRstrDefinitions = useSelector(
    selectClientLossRstrDefinitions(SelectionState.COMMITTED),
  );

  const scan2Definitions = useSelector(
    selectScan2Definitions(SelectionState.COMMITTED),
  );

  const getAuxState = useCallback(() => {
    return {
      selectionState,
      appState,
      analyticsDefinitions: analyticsDefinitions.map((def) => def.copy()),
      bookAnalyticDefinitions: bookAnalyticDefinitions.map((def) => def.copy()),
      attributesDefinitions: attributesDefinitions.map((def) => def.copy()),
      betasDefinitions: betasDefinitions.map((def) => def.copy()),
      scenarioAnalysisDefinitions: scenarioAnalysisDefinitions.map((def) =>
        def.copy(),
      ),
      cfDefinitions: cfDefinitions.map((def) => def.copy()),
      durationToTargetDefinitions: durationToTargetDefinitions.map((def) =>
        def.copy(),
      ),
      factribDefinitions: factribDefinitions.map((def) => def.copy()),
      issueCountDefinitions: issueCountDefinitions.map((def) => def.copy()),
      issuerCountDefinitions: issuerCountDefinitions.map((def) => def.copy()),
      perfReturnsDefinitions: perfReturnsDefinitions.map((def) => def.copy()),
      pivotsDefinitions: pivotsDefinitions.map((def) => def.copy()),
      ratingDefinitions: ratingDefinitions,
      teDefinitions: teDefinitions.map((def) => def.copy()),
      tteDefinitions: tteDefinitions.map((def) => def.copy()),
      lastBuySellDefinitions: lastBuySellDefinitions.map((def) => def.copy()),
      realizedGlDefinitions: realizedGlDefinitions.map((def) => def.copy()),
      clientLossRstrDefinitions: clientLossRstrDefinitions.map((def) =>
        def.copy(),
      ),
      scan2Definitions: scan2Definitions.map((def) => def.copy()),
      enableAsOfDateMisc,
    };
  }, [
    analyticsDefinitions,
    bookAnalyticDefinitions,
    appState,
    attributesDefinitions,
    betasDefinitions,
    scenarioAnalysisDefinitions,
    lastBuySellDefinitions,
    realizedGlDefinitions,
    cfDefinitions,
    durationToTargetDefinitions,
    enableAsOfDateMisc,
    factribDefinitions,
    issueCountDefinitions,
    issuerCountDefinitions,
    perfReturnsDefinitions,
    pivotsDefinitions,
    ratingDefinitions,
    selectionState,
    teDefinitions,
    tteDefinitions,
    scan2Definitions,
    clientLossRstrDefinitions,
  ]);

  /**Handle Aux state from grid state**/
  React.useEffect(() => {
    if (auxStateRequest) {
      let asOfDate;
      if (auxStateReqSymbolicDate?.length) {
        //if aux date has symbolic date, grab the resolved date from the symbolic date query
        asOfDate = auxStateSymbolicDateQuery?.data;
      } else {
        asOfDate = auxStateRequest.selectionState.asOfDate;
      }

      //as of date maybe null if waiting on symbolic date.
      if (!asOfDate) return;

      batch(() => {
        const { appState, selectionState } = auxStateRequest;
        if (appState) {
          dispatch(setAppTitle(appState.appTitle ?? "Account Summary"));
          dispatch(setAppDecimal(appState.appDecimal ?? 2));
          dispatch(
            setListenScenarioAppTalk(appState.listenScenarioAppTalk ?? false),
          );
          dispatch(
            setListenEntityGroupAppTalk(
              appState.listenEntityGroupAppTalk ?? false,
            ),
          );
          // If the hideGridBarOption parameter is true, hide the gridsidebar.
          // If not, set to appState value, which has a default of false.
          dispatch(
            setHideGridSideBar(hideGridBarOption ?? appState.hideGridSideBar),
          );
          dispatch(
            setDisableCustomGroupSort(appState.disableCustomGroupSort ?? false),
          );
          dispatch(setBandedRows(appState.bandedRows ?? true));
          dispatch(setGridVerticalLines(appState.gridVerticalLines ?? true));
          dispatch(setGridFontSize(appState.gridFontSize ?? 12));
          dispatch(setGridRowHeight(appState.gridRowHeight ?? 22));
          dispatch(setGridHeaderHeight(appState.gridHeaderHeight ?? 65));
          dispatch(setMinInceptionDate(appState.minInceptionDate ?? null));
          dispatch(setMinInceptionDate(appState.loadDataOnLaunch ?? true));
        }
        const loadDataOnLaunch = appState.loadDataOnLaunch ?? true;
        dispatch(setLoadDataOnLaunch(loadDataOnLaunch));

        if (selectionState) {
          dispatch(setMode(selectionState.mode ?? Mode.ACC_GROUPS));
          if (loadDataOnLaunch) {
            dispatch(setGroupType(selectionState.groupType));
            dispatch(setGroup(selectionState.groups));
            if (selectionState?.entityIds?.length) {
              dispatch(setEntityIds(selectionState?.entityIds));
            }
          }
          dispatch(setMajorMinor(selectionState.majorMinor));
          dispatch(setDisplayBenchmarks(selectionState.displayBenchmarks));
          dispatch(
            setBenchmarkEntityId(selectionState.benchmarkEntityId ?? null),
          );
          dispatch(setLookthru(selectionState.lookthru ?? true));
          if (Array.isArray(selectionState.scenario)) {
            /* const filteredArray = selectionState.scenario.filter((f) =>
              options.some((item) => item.value === f)
            );
            console.log("filteredarray", filteredArray, options);
            dispatch(setScenariosValue(filteredArray ?? null)); */
            dispatch(setScenariosValue(selectionState.scenario ?? null));
          } else {
            options.filter((i) => i.value === selectionState.scenario).length >
              0 ?? dispatch(setScenariosValue(selectionState.scenario ?? null));
          }
          //dispatch(setScenariosValue(selectionState.scenario ?? null));
          dispatch(setSideBySide(selectionState.sideBySide ?? false));
          dispatch(setGlPam(selectionState.glPam ?? false));
          dispatch(setSymbolicDate(selectionState.symbolicDate ?? null));
          dispatch(setAsOfDate(asOfDate));
          dispatch(
            setBenchmarkScenariosValue(selectionState.benchmarkScenario),
          );
          dispatch(
            setIsDisableBenchmarkWorkspace(
              selectionState.isDisableBenchmarkWorkspace ?? true,
            ),
          );
        }

        dispatch(
          setAnalyticDefinitions(
            auxStateRequest?.analyticsDefinitions?.map(
              (def) =>
                new AnalyticDefinition({
                  ...def,
                  scaleType: def.scaleType ?? "Contribution",
                }),
            ) ?? [],
          ),
        );
        dispatch(
          setAttributesDefinitions(
            auxStateRequest?.attributesDefinitions?.map(
              (def) => new AttributeDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setBetasDefinitions(
            auxStateRequest?.betasDefinitions?.map(
              (def) => new BetaDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setScenarioAnalysisDefinitions(
            auxStateRequest?.scenarioAnalysisDefinitions?.map(
              (def) => new ScenarioAnalysisDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setCfDefinitions(
            auxStateRequest?.cfDefinitions?.map(
              (def) => new CashFlowDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setDurationToTargetDefinitions(
            auxStateRequest?.durationToTargetDefinitions?.map(
              (def) => new DurationToTargetDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setFactribDefinitions(
            auxStateRequest?.factribDefinitions?.map(
              (def) => new FactorAttributionDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setIssueCountDefinitions(
            auxStateRequest?.issueCountDefinitions?.map(
              (def) => new IssueCountDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setIssuerCountDefinitions(
            auxStateRequest?.issuerCountDefinitions?.map(
              (def) => new IssuerCountDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setClientLossRstrDefinitions(
            auxStateRequest?.clientLossRstrDefinitions?.map(
              (def) => new ClientLossRstrDefinition(def),
            ) ?? [],
          ),
        );

        dispatch(
          setPerfReturnsDefinitions(
            auxStateRequest?.perfReturnsDefinitions?.map(
              (def) => new PerfReturnDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setPivotsDefinitions(
            auxStateRequest?.pivotsDefinitions?.map((def) =>
              PivotDefinition.fromObject({
                ...def,
                scaleType: def?.scaleType ?? "Contribution",
              }),
            ) ?? [],
          ),
        );
        dispatch(
          setRatingDefinitions(
            auxStateRequest?.ratingDefinitions?.map(
              (def) => new RatingDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setTeDefinitions(
            auxStateRequest?.teDefinitions?.map(
              (def) => new TrackingErrorDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setTteDefinitions(
            auxStateRequest?.tteDefinitions?.map(
              (def) => new TradeTrackingErrorDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setEnableAsOfDate(auxStateRequest.enableAsOfDateMisc ?? false),
        );
        dispatch(
          setLastBuySellDefinitions(
            auxStateRequest?.lastBuySellDefinitions?.map(
              (def) => new LastBuySellDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setRealizedGlDefinitions(
            auxStateRequest?.realizedGlDefinitions?.map(
              (def) => new RealizedGlDefinitions(def),
            ) ?? [],
          ),
        );
        dispatch(
          setScan2Definitions(
            auxStateRequest?.scan2Definitions?.map(
              (def) => new Scan2Definition(def),
            ) ?? [],
          ),
        );
        dispatch(
          setBookAnalyticDefinitions(
            auxStateRequest?.bookAnalyticDefinitions?.map(
              (def) => new BookAnalyticDefinition(def),
            ) ?? [],
          ),
        );
        dispatch(commitSelections());
        dispatch(toggleSelectionDrawer(false));
        if (loadDataOnLaunch) {
          dispatch(startRequests());
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auxStateReqSymbolicDate?.length,
    auxStateRequest,
    auxStateSymbolicDateQuery?.data,
    dispatch,
  ]);

  const processColumnStateCallback = useProcessColumnStateCallback();
  const processGridState = useCallback((gridState) => {
    if (!gridState) return gridState;
    const sfx = " - scenario";
    const SUMMARY_TOKENS = new Set(["ABSOLUTE", "RELATIVE", "BENCHMARK"]);
    // Build sets from gridState auxState instead of component state (which is empty before load)
    const ANALYTICS_SET = new Set([
      ...(gridState.auxState?.analyticsDefinitions || []).map(
        (d) => d.analytic,
      ),
      ...(gridState.auxState?.pivotsDefinitions || []).map((d) => d.analytics),
    ]);

    const migrateColId = (colId) => {
      if (!colId) return colId;
      const hasSbs = colId.endsWith(sfx);
      const base = hasSbs ? colId.slice(0, -sfx.length) : colId;
      const parts = base.split(",").map((s) => s.trim());

      // Migrate Analytics columns: 2-part format (analytic, summary) -> 3-part (analytic, summary, scaleType)
      if (
        parts.length === 2 &&
        ANALYTICS_SET.has(parts[0]) &&
        SUMMARY_TOKENS.has(parts[1])
      ) {
        const migrated = `${parts[0]},${parts[1]},Contribution`;
        return hasSbs ? migrated + sfx : migrated;
      }

      // Migrate Pivot columns: 3-part format (levels, analytics, summary) -> 4-part (levels, analytics, summary, scaleType)
      // Pivot structure: "RR_SCHEMA_1, weight, RELATIVE" -> "RR_SCHEMA_1, weight, RELATIVE, Contribution"
      if (
        parts.length === 3 &&
        parts[1] &&
        ANALYTICS_SET.has(parts[1]) &&
        SUMMARY_TOKENS.has(parts[2])
      ) {
        const migrated = `${parts[0]},${parts[1]},${parts[2]},Contribution`;
        return hasSbs ? migrated + sfx : migrated;
      }
      return colId;
    };

    // Migrate columnSettingsByColId keys and merge duplicates
    const newColumnSettingsByColId = {};
    const sourceSettings = gridState.columnSettingsByColId || {};
    Object.entries(sourceSettings).forEach(([oldColId, setting]) => {
      const newColId = migrateColId(oldColId);
      const existing = newColumnSettingsByColId[newColId] || {};
      newColumnSettingsByColId[newColId] = {
        ...existing,
        ...setting,
        colId: newColId,
      };
    });

    // Migrate columnState colIds and merge duplicates
    const mergedStatesMap = new Map();
    (gridState.columnState || []).forEach((state) => {
      const newColId = migrateColId(state.colId);
      const existing = mergedStatesMap.get(newColId) || {};
      mergedStatesMap.set(newColId, {
        colId: newColId,
        hide: state.hide ?? existing.hide ?? false,
        width: state.width ?? existing.width,
        pinned: state.pinned ?? existing.pinned,
        sort: state.sort ?? existing.sort,
        sortIndex: state.sortIndex ?? existing.sortIndex,
      });
    });
    const newColumnState = Array.from(mergedStatesMap.values());

    // Migrate filterModel keys if present
    let newFilterModel = gridState.filterModel;
    if (newFilterModel && typeof newFilterModel === "object") {
      const migrated = {};
      Object.entries(newFilterModel).forEach(([oldColId, model]) => {
        migrated[migrateColId(oldColId)] = model;
      });
      newFilterModel = migrated;
    }

    return {
      ...gridState,
      columnSettingsByColId: newColumnSettingsByColId,
      columnState: newColumnState,
      filterModel: newFilterModel,
    };
  }, []);
  const listenScenarioAppTalk = useSelector(selectListenScenarioAppTalk);
  const listenEntityAppTalk = useSelector(selectListenEntityGroupAppTalk);
  const queryClient = useQueryClient();
  const userInvestPlatformIds = useSelector(
    selectUserInvestPlatformIds(SelectionState.COMMITTED),
  );
  /**
   * AppTalk
   */
  useEffect(() => {
    const { in2AppTalk } = window;

    if (!in2AppTalk) {
      return;
    }

    const instance = in2AppTalk.getInstance();

    instance.subscribe("*", (subject, msg) => {
      console.log("Received in2 message: ", subject, "msg: ", msg);
      if (
        listenScenarioAppTalk &&
        subject === in2AppTalk.MSG_TYPE.PRIMARY_STACKED_ITEM_CHANGE &&
        msg.get("gridLoadedWithPersistedScenario")
      ) {
        const scenarioLoadedData = JSON.parse(
          msg.get("gridLoadedWithPersistedScenario"),
        );
        batch(() => {
          dispatch(requestScenarioOptions(userInvestPlatformIds));
          dispatch(setMode(Mode.SCENARIOS));
          dispatch(setScenariosValue(scenarioLoadedData.name));
          dispatch(toggleSelectionDrawer(false));
          queryClient.invalidateQueries(["pivot"]);
          queryClient.invalidateQueries(["pivot-sbs"]);
          queryClient.invalidateQueries(["cashflow"]);
          queryClient.invalidateQueries(["lastBuySell"]);
          queryClient.invalidateQueries(["ratings"]);
          dispatch(commitSelections());
          dispatch(startRequests(true));
        });
      }

      if (
        listenEntityAppTalk &&
        selectionState.groupType === "GROUP" &&
        msg &&
        msg.uniqueIdVal &&
        appTalkSentMessage.current?.uniqueIdVal !== Number(msg.uniqueIdVal)
      ) {
        const entityId = msg.uniqueIdVal;
        dispatch(setGroup([Number(entityId)]));
        if (displayBenchmarksValue) {
          dispatch(setBenchmarkEntityId(Number(entityId)));
        }
        dispatch(toggleSelectionDrawer(false));
        dispatch(commitSelections());
        dispatch(startRequests(false));
      }
    });

    return () => {
      if (in2AppTalk) {
        instance.unsubscribe("*");
      }
    };
  }, [
    listenScenarioAppTalk,
    listenEntityAppTalk,
    selectionState.groupType,
    dispatch,
    queryClient,
    userInvestPlatformIds,
    entityGroupOptions,
    displayBenchmarksValue,
  ]);

  /**
   * Entity attributes used by Attributes selector
   */
  useEffect(() => {
    if (committedAsOfDate) {
      dispatch(requestEntityAttributes(committedAsOfDate));
    }
  }, [committedAsOfDate, dispatch]);

  /**
   * Entities that populate the various entity selectors
   */
  useEffect(() => {
    if (uncommittedAsOfDate) {
      dispatch(requestEntities(uncommittedAsOfDate));
    }
  }, [uncommittedAsOfDate, dispatch]);

  /**
   * Loading mask/message
   */
  const [loading, showSpinner, loadingMessage] = useMemo(() => {
    if (auxStateSymbolicDateQuery.isFetching) {
      return [true, true, `Please wait...`];
    }
    if (fetchingScenarioState) {
      return [true, true, `Checking scenario state. Please wait...`];
    }
    if (fetchingScenarioEntityIds) {
      return [true, true, `Resolving scenario entities. Please wait...`];
    }
    if (fetchingSubmitScenarioEntities) {
      return [true, true, `Submitting scenario entities. Please wait...`];
    }
    if (fetchingDeleteScenarioEntities) {
      return [true, true, `Deleting scenario entities. Please wait...`];
    }
    if (isPivotFetching) {
      return [true, true, `Fetching Pivot Columns. Please wait...`];
    }
    return [false];
  }, [
    auxStateSymbolicDateQuery.isFetching,
    fetchingDeleteScenarioEntities,
    fetchingScenarioEntityIds,
    fetchingScenarioState,
    fetchingSubmitScenarioEntities,
    isPivotFetching,
  ]);
  const disclosureAccpt = () => {
    setDisclaimer("false");
  };
  console.log(
    "APP RENDERING....",
    process.env.REACT_APP_BASE_URL_GRID_STATE_SERVICE,
  );
  return (
    <>
      {disclaimer === "true" ? (
        <Dialog acceptDisclaimer={disclosureAccpt} active={disclaimer} />
      ) : (
        <>
          <GridStateProvider
            appId={process.env.REACT_APP_ID}
            defaultDecimalValue={decimalValue}
            stateId={gridStateId}
            onChangeStateId={onChangeStateId}
            baseURL={
              process.env.REACT_APP_HOST_ENV === "AZURE"
                ? process.env.REACT_APP_BASE_URL_GRID_STATE_SERVICE
                : undefined
            }
            getAuxState={getAuxState}
            setAuxState={setAuxStateRequest}
            getShareUrl={getShareUrl}
            processColumnStateCallback={processColumnStateCallback}
            processGridState={processGridState}
            environment={
              process.env.REACT_APP_HOST_ENV !== "AZURE"
                ? process.env.REACT_APP_IN2_ENV
                : undefined
            }
          >
            <>
              <Home />
              <LoadingMask
                active={loading}
                zIndex={12}
                msg={loadingMessage}
                showMsg
                showSpinner={showSpinner}
              />
            </>
          </GridStateProvider>
        </>
      )}
    </>
  );
};

export default App;
